import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { Close } from "@gigpro/ui/Icons";
import { Stepper, StepperContent, StepperIndicator, StepperItem, StepperNav, StepperPanel } from "@gigpro/ui/Stepper";
import { useNow } from "@gigpro/ui/utils/useNow";
import {
  getHireJobOffersListQueryKey,
  getHireJobsProMatchStatusListQueryKey,
  useHireLocationsDirectHireCreateWithTransform,
} from "@sidegigllc/api/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useJobCreditStats } from "api/queries/hire";
import { useProProfile } from "api/queries/pros";
import { env } from "config/env";
import { addDays } from "date-fns";
import { DirectHireForm, type DirectHireValues, directHireSchema } from "domains/hire/components/DirectHireForm";
import { PayPerHireChoice, type PayPerHireOption } from "domains/hire/components/PayPerHireChoice";
import { useJobsGatedAction } from "domains/hire/containers/JobsGateStep";
import { PaymentSummaryContainer } from "domains/hire/containers/PaymentSummaryContainer";
import { PostPubliclyButton } from "domains/hire/containers/PostPubliclyButton";
import { hireFeePricing } from "domains/hire/hireFeePlan";
import { useJobsGateState } from "domains/hire/hooks/useJobsGateState";
import { useSwitchToJobsPlan } from "domains/hire/hooks/useSwitchToJobsPlan";
import { payPerHireChoiceCopy, payPerHireSwitchFor } from "domains/hire/jobsPlan";
import React from "react";
import { useWatch } from "react-hook-form";
import { captureException } from "utils/errorReporting";
import { toastApiError } from "utils/toastApiError";
import { directHirePreviewQuery, jobTypesQuery, positionsQuery } from "./queries";

interface DirectHireDialogProps {
  open: boolean;
  onClose: () => void;
  locationId: number;
  proId: number;
  proFirstName: string;
  /** Position the form starts on, for callers that already know it (e.g. the shift just rated). */
  defaultPositionId?: number;
  onSuccess?: () => void;
  /** Back to the job picker, for a business that would rather post a job. */
  onPostJobInstead?: () => void;
}

interface DirectHireSentDialogProps {
  open: boolean;
  onClose: () => void;
  proFirstName: string;
  createdJob?: { id: string; name: string };
}

/** The sent screen. Its own component, so the "posted" state unmounts with it and a reopened dialog starts clean. */
const DirectHireSentDialog: React.FC<DirectHireSentDialogProps> = ({ open, onClose, proFirstName, createdJob }) => {
  const [postedPublicly, setPostedPublicly] = React.useState(false);

  return (
    <Dialog bottomSheet open={open} onClose={onClose} className="max-w-md">
      <div className="flex items-center justify-between">
        <h2 className="text-extraLarge font-semibold">Job offer sent</h2>
        <button type="button" onClick={onClose} className="text-brand" aria-label="Close">
          <Close className="size-5" />
        </button>
      </div>
      <p className="text-normal text-black">
        Your job offer has been sent to {proFirstName}. We'll notify you as soon as they respond. Track the status and
        view details of this offer from the tab "Job offered".
      </p>
      {createdJob && !postedPublicly && (
        <PostPubliclyButton
          job={{ ...createdJob, status: "open", is_private: true, origin: "direct_hire" }}
          onPublished={() => setPostedPublicly(true)}
        />
      )}
      <DialogFooter>
        <Button variant="contained" className="w-full" onClick={onClose}>
          Got it
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

/** The "Hire {name} directly" flow: a four-input form, then the payment summary, then the offer goes out. */
export const DirectHireDialogContainer: React.FC<DirectHireDialogProps> = ({
  open,
  onClose,
  locationId,
  proId,
  proFirstName,
  defaultPositionId,
  onSuccess,
  onPostJobInstead,
}) => {
  const queryClient = useQueryClient();
  const [step, setStep] = React.useState<1 | 2>(1);
  const [showSuccess, setShowSuccess] = React.useState(false);
  // The private job the offer created, so the sent screen can offer to post it publicly
  const [createdJob, setCreatedJob] = React.useState<{ id: string; name: string }>();
  const [payOption, setPayOption] = React.useState<PayPerHireOption>("fee");
  // Covers the refetches after the create, when the mutation is no longer pending but the offer is not done
  const [isSending, setIsSending] = React.useState(false);
  // Held for the whole switch-then-send, since the plan refetch after the switch leaves a gap where nothing is pending
  const [isSwitching, setIsSwitching] = React.useState(false);

  const { data: positions } = useQuery({ ...positionsQuery(), enabled: open });
  const { data: jobTypes } = useQuery({ ...jobTypesQuery(), enabled: open, select: (data) => data.results });

  const mountedAt = useNow({ updateIntervalMs: false });
  const form = useZodForm({
    schema: directHireSchema,
    defaultValues: { position: defaultPositionId, expires_at: addDays(mountedAt, 3) },
  });
  const [positionId, jobTypeId] = useWatch({ control: form.control, name: ["position", "job_type"] });
  const fullTimeSelected = !!jobTypes?.find((jobType) => jobType.id === jobTypeId)?.name.includes("Full-time");

  const previewQuery = directHirePreviewQuery({ locationId, proId, positionId });
  const { data: preview } = useQuery({ ...previewQuery, enabled: open && previewQuery.enabled });
  const defaultWage = preview?.default_wage;
  // Reading dirtyFields subscribes the form to dirty tracking, which getFieldState alone does not
  const wageDirty = !!form.formState.dirtyFields.wage;
  // The wage is only a starting point from the preview: never overwrite what the business typed
  React.useEffect(() => {
    if (!wageDirty) {
      form.setValue("wage", defaultWage ?? ("" as unknown as number));
    }
  }, [defaultWage, wageDirty, form]);

  const { data: proData, isLoading: isLoadingPro } = useProProfile(proId, { enabled: open });
  const hireFee = proData?.hire_fee_data ?? undefined;
  // Jobs plan pricing skips credits, so it never waits on them.
  const usesCredits = hireFeePricing(hireFee) === "legacy";
  const {
    data: creditStats,
    isLoading: isLoadingCredits,
    isError: isCreditsError,
  } = useJobCreditStats({ enabled: usesCredits });

  const { run: runGated, step: gateStep, ready: gateReady } = useJobsGatedAction(locationId);
  const { status: planStatus, canManage: canManagePlan } = useJobsGateState(locationId);
  const switchOption = payPerHireSwitchFor(planStatus);
  const switchPlan = useSwitchToJobsPlan(locationId);
  const switching = !!switchOption && canManagePlan && payOption === "switch";
  // Don't send before the price the business is agreeing to has rendered, or the Jobs plan check can answer.
  const canProceed =
    gateReady && !isLoadingPro && (!usesCredits || (!isLoadingCredits && !isCreditsError && !!creditStats));

  const createMutation = useHireLocationsDirectHireCreateWithTransform();

  const sendOffer = async (values: DirectHireValues) => {
    setIsSending(true);
    try {
      const offer = await createMutation.mutateAsync({ locationId, data: { pro_id: proId, ...values } });
      setCreatedJob({ id: offer.job_posting_id, name: offer.job_posting_name });
      await Promise.all([
        queryClient.refetchQueries({ queryKey: getHireJobsProMatchStatusListQueryKey() }),
        queryClient.refetchQueries({ queryKey: getHireJobOffersListQueryKey() }),
      ]);
      setShowSuccess(true);
      onSuccess?.();
    } catch (error) {
      toastApiError(error);
      captureException(error);
    } finally {
      setIsSending(false);
    }
  };

  const handleConfirm = form.handleSubmit(async (values) => {
    if (!canProceed) return;
    if (switching) {
      setIsSwitching(true);
      // All or nothing: a refused switch leaves the location on pay per hire and creates no job
      try {
        await switchPlan.mutateAsync();
      } catch (error) {
        toastApiError(error);
        setIsSwitching(false);
        return;
      }
      await sendOffer(values);
      setIsSwitching(false);
      return;
    }
    // A location without the Jobs plan sees the trial before anything is created
    await runGated(() => sendOffer(values));
  });

  const handleClose = () => {
    setStep(1);
    setShowSuccess(false);
    setCreatedJob(undefined);
    setPayOption("fee");
    form.reset();
    onClose();
  };

  if (gateStep) {
    return (
      <Dialog bottomSheet open={open} onClose={handleClose} className="max-w-md">
        {gateStep}
      </Dialog>
    );
  }

  if (showSuccess) {
    return (
      <DirectHireSentDialog open={open} onClose={handleClose} proFirstName={proFirstName} createdJob={createdJob} />
    );
  }

  const positionName = positions?.find((position) => position.id === positionId)?.name ?? "";
  const feeSummary = (
    <PaymentSummaryContainer
      job={{ location: locationId, name: positionName }}
      hireFee={hireFee}
      isLoadingHireFee={isLoadingPro}
      proFirstName={proFirstName}
    />
  );
  const isPending = createMutation.isPending || isSwitching || isSending;

  return (
    <Dialog bottomSheet open={open} onClose={handleClose} className="max-w-xl" contentClassName="gap-0">
      <DialogTitle className="pb-0 text-extraLarge font-semibold">
        {step === 1 ? `Hire ${proFirstName} directly` : "Payment Summary"}
      </DialogTitle>

      <Stepper value={step}>
        <StepperNav>
          <StepperItem step={1}>
            <StepperIndicator className="h-1 w-full" />
          </StepperItem>
          <StepperItem step={2}>
            <StepperIndicator className="h-1 w-full" />
          </StepperItem>
        </StepperNav>

        <StepperPanel className="pt-1">
          <StepperContent value={1}>
            <DirectHireForm
              form={form}
              proName={proFirstName}
              positions={positions}
              jobTypes={jobTypes}
              bookedShiftCount={preview?.booked_shift_count}
              fullTimeSelected={fullTimeSelected}
              onSubmit={() => setStep(2)}
              onCancel={handleClose}
              onPostJobInstead={onPostJobInstead}
            />
          </StepperContent>

          <StepperContent value={2} className="flex flex-col gap-2">
            {usesCredits && (
              <p className="text-normal text-black">Review how credits and charges will apply to this job offer.</p>
            )}

            {switchOption ? (
              <PayPerHireChoice
                copy={payPerHireChoiceCopy(switchOption, "this location")}
                canSwitch={canManagePlan}
                value={payOption}
                onValueChange={setPayOption}
                disabled={isPending}
                feeSummary={feeSummary}
              />
            ) : (
              feeSummary
            )}

            <div className="flex flex-col items-center gap-1 pt-2">
              <Button
                variant="contained"
                className="w-full"
                onClick={handleConfirm}
                disabled={isPending || !canProceed}
                loading={isPending}
              >
                Confirm and send offer
              </Button>
              <Button variant="text" onClick={() => setStep(1)} disabled={isPending}>
                Back
              </Button>
              <Button variant="text" asChild>
                <a href={env.REACT_APP_MARKETPLACE_PLATFORM_TERMS_URL} target="_blank" rel="noopener noreferrer">
                  View Terms of Service
                </a>
              </Button>
            </div>
          </StepperContent>
        </StepperPanel>
      </Stepper>
    </Dialog>
  );
};
